import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";

const DEFAULT_CENTER = { lat: -25.291, lng: -57.638 };
let mapsLoader = null;

const toLatLng = (position) => {
  if (!position) return null;
  const lat = typeof position.lat === "function" ? position.lat() : position.lat;
  const lng = typeof position.lng === "function" ? position.lng() : position.lng;
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }
  if (!apiKey) {
    return Promise.reject(new Error("missing-key"));
  }
  if (!mapsLoader) {
    mapsLoader = new Promise((resolve, reject) => {
      let settled = false;
      const succeed = () => {
        if (settled) return;
        settled = true;
        resolve(window.google);
      };
      const fail = (error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      window.gm_authFailure = () => fail(new Error("api"));

      const existing = document.querySelector("script[data-reva-google-maps='async']");
      if (existing) {
        if (window.google?.maps?.importLibrary) {
          succeed();
          return;
        }
        existing.addEventListener("load", succeed);
        existing.addEventListener("error", () => fail(new Error("maps-script-failed")));
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async`;
      script.async = true;
      script.dataset.revaGoogleMaps = "async";
      script.onload = succeed;
      script.onerror = () => fail(new Error("maps-script-failed"));
      document.head.appendChild(script);
    });
  }
  return mapsLoader;
};

const attachPlaceSearch = async (google, map, host, placeholder, placeMarker) => {
  const places = await google.maps.importLibrary("places");
  const PlaceAutocompleteElement = places.PlaceAutocompleteElement;
  if (typeof PlaceAutocompleteElement !== "function") {
    throw new Error("places-widget-unavailable");
  }

  const autocomplete = new PlaceAutocompleteElement({ placeholder });
  autocomplete.style.width = "100%";
  host.replaceChildren(autocomplete);

  const onSelect = async (event) => {
    const place = event.placePrediction?.toPlace?.() || event.place;
    if (!place) return;
    if (typeof place.fetchFields === "function") {
      await place.fetchFields({ fields: ["formattedAddress", "location", "viewport", "displayName"] });
    }
    if (place.viewport) {
      map.fitBounds(place.viewport);
    }
    placeMarker(place.location, {
      address: place.formattedAddress || place.displayName || undefined,
    });
  };

  autocomplete.addEventListener("gmp-select", onSelect);
  autocomplete.addEventListener("gmp-placeselect", onSelect);
  return autocomplete;
};

const VenueMapPicker = ({ lat, lng, onChange }) => {
  const intl = useIntl();
  const mapRef = useRef(null);
  const searchHostRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [loadError, setLoadError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    let autocomplete = null;
    let clickListener = null;

    const boot = async () => {
      const google = await loadGoogleMaps(apiKey);
      if (cancelled || !mapRef.current) return;

      const { Map } = await google.maps.importLibrary("maps");

      const hasPin = lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
      const center = hasPin ? { lat: Number(lat), lng: Number(lng) } : DEFAULT_CENTER;
      const map = new Map(mapRef.current, {
        zoom: hasPin ? 16 : 13,
        center,
        panControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });

      markerRef.current = hasPin ? new google.maps.Marker({ position: center, map }) : null;

      const placeMarker = (position, extra = {}) => {
        const next = toLatLng(position);
        if (!next) return;
        if (!markerRef.current) {
          markerRef.current = new google.maps.Marker({ position: next, map });
        } else {
          markerRef.current.setPosition(next);
        }
        map.panTo(next);
        onChangeRef.current({ lat: next.lat, lng: next.lng, ...extra });
      };

      clickListener = map.addListener("click", (event) => placeMarker(event.latLng));
      setLoadError(null);

      if (!searchHostRef.current) return;
      try {
        autocomplete = await attachPlaceSearch(
          google,
          map,
          searchHostRef.current,
          intl.formatMessage({ id: "gestion.venues.mapSearch" }),
          placeMarker,
        );
      } catch (error) {
        if (!cancelled) setSearchError("places");
      }
    };

    if (!apiKey) {
      setLoadError("missing-key");
      return undefined;
    }

    boot().catch(() => {
      if (!cancelled) setLoadError("api");
    });

    return () => {
      cancelled = true;
      const maps = window.google?.maps;
      if (clickListener && maps?.event) {
        maps.event.removeListener(clickListener);
      }
      autocomplete?.remove?.();
    };
    // Initialize once per mount; parent remounts the picker when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const errorMessage =
    loadError === "missing-key"
      ? intl.formatMessage({ id: "gestion.venues.mapMissingKey" })
      : loadError === "api"
        ? intl.formatMessage({ id: "gestion.venues.mapApiNotActivated" })
        : searchError
          ? intl.formatMessage({ id: "gestion.venues.mapSearchUnavailable" })
          : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        ref={searchHostRef}
        sx={{
          width: "100%",
          "& gmp-place-autocomplete": { width: "100%" },
        }}
      />
      {errorMessage && (
        <Box sx={{ mt: 1, color: searchError && !loadError ? "warning.main" : "error.main", fontSize: 13 }}>
          {errorMessage}
        </Box>
      )}
      <Box ref={mapRef} sx={{ mt: 1, height: 240, width: "100%", borderRadius: 1, bgcolor: "action.hover" }} />
    </Box>
  );
};

export default VenueMapPicker;
